import {messages} from '@smartmixin/i18n';
import {NormalizedForecastModel} from './types';

// Some models below are integrated end-to-end (backend, i18n, logos) but are
// commented out to withhold them from the current mobile release; they ship in
// a later one. Re-enable one by deleting its `// ` prefixes and the marker line
// above it — nothing else was removed, so no other file needs touching.
export const SupportedForecastModelList = [
  {
    key: 'GFS',
    premium: false,
    serverId: {
      forecastModel: 'GFS',
      meshSize: null,
    },
    type: 'global',
    meshSize: 25,
    title: 'GFS',
    description: messages.common.forecastModelDescription_GFS,
    editor: 'NOAA',
    horizon: 384,
    frequency: '00, 06, 12, 18 UTC',
    link: 'https://www.ncei.noaa.gov/products/weather-climate-models/global-forecast',
  },
  {
    key: 'ECMWF_HRES',
    premium: true,
    serverId: {
      forecastModel: 'ECMWF_HRES',
      meshSize: '0P25',
    },
    type: 'global',
    meshSize: 25,
    title: 'ECMWF HRES',
    description: messages.common.forecastModelDescription_ECMWF_HRES,
    editor: 'ECMWF',
    horizon: 360,
    frequency: '00, 06, 12, 18 UTC',
    link: 'https://www.ecmwf.int/en/forecasts/datasets/open-data',
  },
  {
    key: 'HRRR_CONUS',
    premium: true,
    serverId: {
      forecastModel: 'HRRR_CONUS',
      meshSize: '0P03',
    },
    type: 'regional',
    meshSize: 3,
    title: 'HRRR',
    description: messages.common.forecastModelDescription_HRRR_CONUS,
    editor: 'NOAA',
    horizon: 18,
    frequency: 'hourly',
    link: 'https://www.ncei.noaa.gov/products/weather-climate-models/rapid-refresh-update',
  },
  {
    key: 'RRFS_CONUS',
    premium: true,
    serverId: {
      forecastModel: 'RRFS_CONUS',
      meshSize: '0P03',
    },
    type: 'regional',
    meshSize: 3,
    title: 'RRFS CONUS',
    description: messages.common.forecastModelDescription_RRFS_CONUS,
    editor: 'NOAA',
    // The synoptic cycles reach 84 h; the four intermediate ones stop at 18 h.
    // `horizon` is a single number, so it states the maximum.
    horizon: 84,
    frequency: '00, 03, 06, 09, 12, 15, 18, 21 UTC',
    link: 'https://www.emc.ncep.noaa.gov/emc/pages/numerical_forecast_systems/rrfs.php',
  },
  {
    key: 'RRFS_NORTH_AMERICA',
    premium: true,
    serverId: {
      forecastModel: 'RRFS_NORTH_AMERICA',
      meshSize: '0P11',
    },
    type: 'regional',
    meshSize: 13,
    title: 'RRFS North America',
    description: messages.common.forecastModelDescription_RRFS_NORTH_AMERICA,
    editor: 'NOAA',
    horizon: 84,
    frequency: '00, 06, 12, 18 UTC',
    link: 'https://www.emc.ncep.noaa.gov/emc/pages/numerical_forecast_systems/rrfs.php',
  },
  {
    key: 'EC_RDPS',
    premium: true,
    serverId: {
      forecastModel: 'EC_RDPS',
      meshSize: '0P10',
    },
    type: 'regional',
    meshSize: 10,
    title: 'RDPS (Regional)',
    description: messages.common.forecastModelDescription_EC_RDPS,
    editor: 'Environment Canada',
    horizon: 80,
    frequency: '00, 06, 12, 18 UTC',
    link: 'https://www.ncei.noaa.gov/products/weather-climate-models/north-american-mesoscale',
  },
  {
    key: 'EC_GDPS',
    premium: true,
    serverId: {
      forecastModel: 'EC_GDPS',
      meshSize: '0P15',
    },
    type: 'global',
    meshSize: 15,
    title: 'GDPS (Global)',
    description: messages.common.forecastModelDescription_EC_GDPS,
    editor: 'Environment Canada',
    horizon: 240,
    frequency: '00, 12 UTC',
    link: 'https://www.ncei.noaa.gov/products/weather-climate-models/north-american-mesoscale',
  },
  // --- Withheld from the current mobile release (see the note above). ---
  // {
  //   key: 'EC_HRDPS',
  //   premium: true,
  //   serverId: {
  //     forecastModel: 'EC_HRDPS',
  //     meshSize: '0P0225',
  //   },
  //   type: 'regional',
  //   meshSize: 2.5,
  //   title: 'HRDPS (High-Res)',
  //   description: messages.common.forecastModelDescription_EC_HRDPS,
  //   editor: 'Environment Canada',
  //   horizon: 48,
  //   frequency: '00, 06, 12, 18 UTC',
  //   link: 'https://eccc-msc.github.io/open-data/msc-data/nwp_hrdps/readme_hrdps-datamart_en/',
  // },
  // --- Withheld from the current mobile release (see the note above). ---
  // {
  //   key: 'DMI_DINI',
  //   premium: true,
  //   serverId: {
  //     forecastModel: 'DMI_DINI',
  //     meshSize: '0P02',
  //   },
  //   type: 'regional',
  //   meshSize: 2,
  //   title: 'HARMONIE DINI',
  //   description: messages.common.forecastModelDescription_DMI_DINI,
  //   editor: 'DMI',
  //   horizon: 60,
  //   frequency: '00, 03, 06, 09, 12, 15, 18, 21 UTC',
  //   link: 'https://opendatadocs.dmi.govcloud.dk/Data/Forecast_Data_Weather_Model_HARMONIE_DINI_SF',
  // },
  {
    key: 'AROME',
    premium: true,
    serverId: {
      forecastModel: 'AROME',
      meshSize: '0P01',
    },
    type: 'regional',
    meshSize: 1.3,
    title: 'AROME',
    description: messages.common.forecastModelDescription_AROME,
    editor: 'Météo France',
    horizon: 52,
    frequency: '00, 03, 06, 09, 12, 15, 18, 21 UTC',
    link: 'https://meteofrance.com/modeles-prevision-meteo',
  },
  {
    key: 'MF_AROME_ANTILLES',
    premium: true,
    serverId: {
      forecastModel: 'MF_AROME_ANTILLES',
      meshSize: '0P025',
    },
    type: 'regional',
    meshSize: 2.5,
    title: 'AROME Antilles',
    description: messages.common.forecastModelDescription_MF_AROME_ANTILLES,
    editor: 'Météo France',
    horizon: 49,
    frequency: '00, 06, 12, 18 UTC',
    link: 'https://meteofrance.com/modeles-prevision-meteo',
  },
  {
    key: 'MF_AROME_GUYANE',
    premium: true,
    serverId: {
      forecastModel: 'MF_AROME_GUYANE',
      meshSize: '0P025',
    },
    type: 'regional',
    meshSize: 2.5,
    title: 'AROME Guyane',
    description: messages.common.forecastModelDescription_MF_AROME_GUYANE,
    editor: 'Météo France',
    horizon: 49,
    frequency: '00, 06, 12, 18 UTC',
    link: 'https://meteofrance.com/modeles-prevision-meteo',
  },
  {
    key: 'MF_AROME_REUNION_MAYOTTE',
    premium: true,
    serverId: {
      forecastModel: 'MF_AROME_REUNION_MAYOTTE',
      meshSize: '0P025',
    },
    type: 'regional',
    meshSize: 2.5,
    title: 'AROME Indien',
    description:
      messages.common.forecastModelDescription_MF_AROME_REUNION_MAYOTTE,
    editor: 'Météo France',
    horizon: 49,
    frequency: '00, 06, 12, 18 UTC',
    link: 'https://meteofrance.com/modeles-prevision-meteo',
  },
  {
    key: 'MF_AROME_POLYNESIE',
    premium: true,
    serverId: {
      forecastModel: 'MF_AROME_POLYNESIE',
      meshSize: '0P025',
    },
    type: 'regional',
    meshSize: 2.5,
    title: 'AROME Polynésie',
    description: messages.common.forecastModelDescription_MF_AROME_POLYNESIE,
    editor: 'Météo France',
    horizon: 49,
    frequency: '00, 06, 12, 18 UTC',
    link: 'https://meteofrance.com/modeles-prevision-meteo',
  },
  {
    key: 'MF_AROME_NOUVELLE_CALEDONIE',
    premium: true,
    serverId: {
      forecastModel: 'MF_AROME_NOUVELLE_CALEDONIE',
      meshSize: '0P025',
    },
    type: 'regional',
    meshSize: 2.5,
    title: 'AROME Nouvelle Calédonie',
    description:
      messages.common.forecastModelDescription_MF_AROME_NOUVELLE_CALEDONIE,
    editor: 'Météo France',
    horizon: 49,
    frequency: '00, 06, 12, 18 UTC',
    link: 'https://meteofrance.com/modeles-prevision-meteo',
  },
  {
    key: 'ARPEGE_EU',
    premium: true,
    serverId: {
      forecastModel: 'ARPEGE',
      meshSize: '0P10',
    },
    type: 'regional',
    meshSize: 10,
    title: 'ARPEGE Europe',
    description: messages.common.forecastModelDescription_ARPEGE_EU,
    editor: 'Météo France',
    horizon: 102,
    frequency: '00, 06, 12, 18 UTC',
    link: 'https://meteofrance.com/modeles-prevision-meteo',
  },
  {
    key: 'ARPEGE_GLOBAL',
    premium: true,
    serverId: {
      forecastModel: 'ARPEGE',
      meshSize: '0P25',
    },
    type: 'global',
    meshSize: 25,
    title: 'ARPEGE Global',
    description: messages.common.forecastModelDescription_ARPEGE_GLOBAL,
    editor: 'Météo France',
    horizon: 102,
    frequency: '00, 06, 12, 18 UTC',
    link: 'https://meteofrance.com/modeles-prevision-meteo',
  },
  // --- Withheld from the current mobile release (see the note above). ---
  // {
  //   key: 'ICON',
  //   premium: true,
  //   serverId: {
  //     forecastModel: 'ICON',
  //     meshSize: '0P125',
  //   },
  //   type: 'global',
  //   meshSize: 13,
  //   title: 'ICON',
  //   description: messages.common.forecastModelDescription_ICON,
  //   editor: 'DWD',
  //   horizon: 180,
  //   frequency: '00, 06, 12, 18 UTC',
  //   link: 'https://www.dwd.de/EN/ourservices/nwp_forecast_data/nwp_forecast_data.html',
  // },
  {
    key: 'ICON_EU',
    premium: true,
    serverId: {
      forecastModel: 'ICON_EU',
      meshSize: null,
    },
    type: 'regional',
    meshSize: 7,
    title: 'ICON-EU',
    description: messages.common.forecastModelDescription_ICON_EU,
    editor: 'DWD',
    horizon: 120,
    frequency: '00, 06, 12, 18 UTC',
    link: 'https://www.dwd.de/EN/ourservices/nwp_forecast_data/nwp_forecast_data.html',
  },
  {
    key: 'ICON_DE',
    premium: true,
    serverId: {
      forecastModel: 'ICON_DE',
      meshSize: null,
    },
    type: 'regional',
    meshSize: 2.2,
    title: 'ICON-D2',
    description: messages.common.forecastModelDescription_ICON_DE,
    editor: 'DWD',
    horizon: 27,
    frequency: '00, 12 UTC',
    link: 'https://www.dwd.de/EN/ourservices/nwp_forecast_data/nwp_forecast_data.html',
  },
  // --- Withheld from the current mobile release (see the note above). ---
  // {
  //   key: 'ICON_2I',
  //   premium: true,
  //   serverId: {
  //     forecastModel: 'ICON_2I',
  //     meshSize: '0P02',
  //   },
  //   type: 'regional',
  //   meshSize: 2.2,
  //   title: 'ICON-2I',
  //   description: messages.common.forecastModelDescription_ICON_2I,
  //   editor: 'ItaliaMeteo / ARPAE',
  //   horizon: 72,
  //   frequency: '00, 12 UTC',
  //   link: 'https://meteohub.agenziaitaliameteo.it/',
  // },
  // --- Withheld from the current mobile release (see the note above). ---
  // {
  //   key: 'ICON_CH2',
  //   premium: true,
  //   serverId: {
  //     forecastModel: 'ICON_CH2',
  //     meshSize: '0P02',
  //   },
  //   type: 'regional',
  //   meshSize: 2.1,
  //   title: 'ICON-CH2',
  //   description: messages.common.forecastModelDescription_ICON_CH2,
  //   editor: 'MeteoSwiss',
  //   horizon: 120,
  //   frequency: '00, 06, 12, 18 UTC',
  //   link: 'https://opendatadocs.meteoswiss.ch/e-forecast-data/e2-e3-numerical-weather-forecasting-model',
  // },
  // --- Withheld from the current mobile release (see the note above). ---
  // {
  //   key: 'ICON_CH1',
  //   premium: true,
  //   serverId: {
  //     forecastModel: 'ICON_CH1',
  //     meshSize: '0P01',
  //   },
  //   type: 'regional',
  //   meshSize: 1,
  //   title: 'ICON-CH1',
  //   description: messages.common.forecastModelDescription_ICON_CH1,
  //   editor: 'MeteoSwiss',
  //   horizon: 33,
  //   frequency: '00, 03, 06, 09, 12, 15, 18, 21 UTC',
  //   link: 'https://opendatadocs.meteoswiss.ch/e-forecast-data/e2-e3-numerical-weather-forecasting-model',
  // },
  {
    key: 'MET_NORWAY',
    premium: true,
    serverId: {
      forecastModel: 'MET_NORWAY',
      meshSize: null,
    },
    type: 'global',
    meshSize: null,
    title: 'MET Norway',
    description: messages.common.forecastModelDescription_MET_NORWAY,
    editor: 'Meteorologisk institutt',
    horizon: 240,
    frequency: null,
    link: 'https://www.met.no/en',
  },
  {
    key: 'WEATHERFLOW_FORECAST',
    premium: true,
    serverId: {
      forecastModel: 'WEATHERFLOW_FORECAST',
      meshSize: null,
    },
    type: 'global',
    meshSize: null,
    title: 'Weatherflow Forecast',
    description: messages.common.forecastModelDescription_WEATHERFLOW_FORECAST,
    editor: 'Weatherflow',
    horizon: 240,
    frequency: null,
    link: 'https://weatherflow.com/tempest-forecasting/',
  },
  {
    key: 'SMARTMIXIN_MULTIMODEL',
    premium: true,
    serverId: {
      forecastModel: 'SMARTMIXIN_MULTIMODEL',
      meshSize: null,
    },
    type: 'global',
    meshSize: null,
    title: 'SmartMixin multi-model',
    description: messages.common.forecastModelDescription_SMARTMIXIN_MULTIMODEL,
    editor: 'SmartMixin',
    horizon: 240,
    frequency: null,
    link: null,
  },
] as NormalizedForecastModel[];
